/**
 * GroupDetailsDialog Component
 * Shows detailed information about a group with tabbed interface
 *
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import { Button, Tabs, Tab, DialogContent, Box, Chip } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from '../ui/BaseDialog';
import { useGroupDetails } from '@/hooks/business/useGroupDetails';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { GroupDetailsHeader } from './GroupDetailsHeader';
import { GroupOverviewTab } from './GroupOverviewTab';
import { GroupMembersTab } from './GroupMembersTab';
import { EditGroupDialog } from './EditGroupDialog';
import type { AppGroup } from '../../types/group';

interface GroupDetailsDialogProps {
  group: AppGroup;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const GroupDetailsDialog: React.FC<GroupDetailsDialogProps> = ({
  group,
  open,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const {
    activeTab,
    setActiveTab,
    syncing,
    error,
    handleSyncMembers,
  } = useGroupDetails(open);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { can } = usePermissions();

  const isExternalGroup = (group.source ?? 'local') !== 'local';

  // Check if current user can manage group members
  const canManageMembers = (() => {
    // Admin and moderators can manage all groups
    if (can.canCreateGroups) {
      return true;
    }

    // Only local groups can be managed (external groups are read-only)
    return !isExternalGroup;
  })();

  const onSyncMembers = () => handleSyncMembers(group, onUpdate);

  const handleEditGroup = () => {
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    onUpdate();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <GroupOverviewTab group={group} />;
      case 1:
        return (
          <GroupMembersTab
            group={group}
            syncing={syncing}
            onSyncMembers={onSyncMembers}
            canManageMembers={canManageMembers}
          />
        );
      default:
        return null;
    }
  };

  const memberCount = (group.members || []).length;

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title="" // Using custom header
        maxWidth="lg"
        fullWidth
        error={error}
        actions={
          <>
            {!isExternalGroup && (
              <Button startIcon={<EditIcon />} onClick={handleEditGroup}>
                {t('groups.actions.editGroup')}
              </Button>
            )}
            <Button onClick={onClose}>{t('groups.details.close')}</Button>
          </>
        }
      >
        <GroupDetailsHeader group={group} onClose={onClose} />

        <DialogContent sx={{ pt: 0 }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
            <Tab label={t('groups.details.tabs.overview')} />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {t('groups.details.tabs.members')}
                  {memberCount > 0 && (
                    <Chip
                      size="small"
                      label={memberCount}
                      sx={{ height: 20, minWidth: 20, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>

          {renderTabContent()}
        </DialogContent>
      </BaseDialog>

      <EditGroupDialog
        group={group}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};
